import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

const ProductRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('pending');

  useEffect(() => {
    fetchRequests();
    axios.post(`${config.API_URL}/admin/inbox-seen`, { type: 'stock_request' }).catch(() => {});
  }, [filterStatus]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const url = filterStatus === 'all' 
        ? `${config.API_URL}/product-requests/admin/all`
        : `${config.API_URL}/product-requests/admin/all?status=${filterStatus}`;
      
      const response = await axios.get(url);
      setRequests(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product requests:', error);
      setLoading(false);
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await axios.put(`${config.API_URL}/product-requests/admin/${requestId}`, {
        status: newStatus
      });
      fetchRequests();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleUpdateNotes = async () => {
    if (!selectedRequest) return;
    
    try {
      await axios.put(`${config.API_URL}/product-requests/admin/${selectedRequest._id}`, {
        notes: notes,
        status: status
      });
      setShowModal(false);
      setSelectedRequest(null);
      setNotes('');
      fetchRequests();
    } catch (error) {
      console.error('Error updating notes:', error);
      alert('Failed to update notes');
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) {
      return;
    }

    try {
      await axios.delete(`${config.API_URL}/product-requests/admin/${requestId}`);
      fetchRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('Failed to delete request');
    }
  };

  const openModal = (request) => {
    setSelectedRequest(request);
    setNotes(request.notes || '');
    setStatus(request.status);
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'bg-warning', text: 'Pending' },
      contacted: { class: 'bg-info', text: 'Contacted' },
      fulfilled: { class: 'bg-success', text: 'Fulfilled' },
      cancelled: { class: 'bg-secondary', text: 'Cancelled' }
    };
    const badge = badges[status] || badges.pending;
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  const getStatusCounts = () => {
    const counts = {
      all: requests.length,
      pending: requests.filter(r => r.status === 'pending').length,
      contacted: requests.filter(r => r.status === 'contacted').length,
      fulfilled: requests.filter(r => r.status === 'fulfilled').length,
      cancelled: requests.filter(r => r.status === 'cancelled').length
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="product-requests-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2><i className="bi bi-inbox me-2" />Stock Request</h2>
        <button 
          className="btn btn-primary"
          onClick={fetchRequests}
        >
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>

      {/* Status Filter Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            All ({statusCounts.all})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${filterStatus === 'pending' ? 'active' : ''}`}
            onClick={() => setFilterStatus('pending')}
          >
            Pending ({statusCounts.pending})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${filterStatus === 'contacted' ? 'active' : ''}`}
            onClick={() => setFilterStatus('contacted')}
          >
            Contacted ({statusCounts.contacted})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${filterStatus === 'fulfilled' ? 'active' : ''}`}
            onClick={() => setFilterStatus('fulfilled')}
          >
            Fulfilled ({statusCounts.fulfilled})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${filterStatus === 'cancelled' ? 'active' : ''}`}
            onClick={() => setFilterStatus('cancelled')}
          >
            Cancelled ({statusCounts.cancelled})
          </button>
        </li>
      </ul>

      {/* Requests List */}
      {requests.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-inbox fs-1 text-muted"></i>
            <p className="text-muted mt-3">No product requests found</p>
          </div>
        </div>
      ) : (
        <div className="row">
          {requests.map((request) => (
            <div key={request._id} className="col-md-6 col-lg-4 mb-4">
              <div className="card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  {getStatusBadge(request.status)}
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(request._id)}
                    title="Delete"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
                <div className="card-body">
                  <h6 className="card-title">{request.productName}</h6>
                  <p className="card-text mb-2">
                    <strong>Customer:</strong> {request.customerName}
                  </p>
                  <p className="card-text mb-2">
                    <strong>Mobile:</strong>{' '}
                    <a href={`tel:${request.mobileNumber}`} className="text-decoration-none">
                      {request.mobileNumber}
                    </a>
                  </p>
                  {request.notes && (
                    <p className="card-text mb-2">
                      <strong>Notes:</strong> {request.notes}
                    </p>
                  )}
                  <small className="text-muted">
                    Requested: {new Date(request.createdAt).toLocaleString()}
                  </small>
                </div>
                <div className="card-footer">
                  <div className="btn-group w-100" role="group">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => openModal(request)}
                    >
                      <i className="bi bi-pencil me-1"></i>
                      Edit
                    </button>
                    {request.status !== 'contacted' && (
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => handleStatusChange(request._id, 'contacted')}
                      >
                        Contacted
                      </button>
                    )}
                    {request.status !== 'fulfilled' && (
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => handleStatusChange(request._id, 'fulfilled')}
                      >
                        Fulfilled
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {showModal && selectedRequest && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Product Request</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedRequest(null);
                    setNotes('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Customer Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedRequest.customerName}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Mobile Number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedRequest.mobileNumber}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Product Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedRequest.productName}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="contacted">Contacted</option>
                    <option value="fulfilled">Fulfilled</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this request..."
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedRequest(null);
                    setNotes('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateNotes}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductRequests;

